import { FC } from 'react';
import {
  Button,
  ButtonGroup,
  ModalIcon,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';

import { GenericModal, GenericModalProps } from '..';

export type FileConflictModalProps = Pick<GenericModalProps, 'modalRef'> & {
  /** Names the user picked that would overwrite existing files. */
  conflicts: string[];
  onCancel: () => void;
  onOverwrite: () => void;
};

/**
 * Shown when an upload would replace files that already exist. Entirely client-side —
 * the file list is already loaded, so detecting a collision needs no request.
 */
export const FileConflictModal: FC<FileConflictModalProps> = ({
  modalRef,
  conflicts,
  onCancel,
  onOverwrite,
}) => (
  <GenericModal
    test-id="ps-file-conflict-modal"
    modalRef={modalRef}
    onClose={onCancel}
  >
    <Stack justifyContent="center">
      <ModalIcon name="error" />
    </Stack>
    <TextTitle size="small" textAlign="center">
      <TranslationComponent i18nKey="pro-studio/actions:file_conflict_modal.title">
        Some Scripts Already Exist
      </TranslationComponent>
    </TextTitle>
    <Spacer size="small" />
    <TextBody
      test-id="ps-file-conflict-modal-subtitle"
      variant="secondary"
      textAlign="center"
    >
      <TranslationComponent i18nKey="pro-studio/actions:file_conflict_modal.subtitle">
        Continuing will replace the matching scripts and permanently remove any
        unsaved edits. You can rename files before upload to avoid overwriting.
      </TranslationComponent>
    </TextBody>
    <Spacer size="large" />
    <TextBody variant="secondary">
      <TranslationComponent i18nKey="pro-studio/actions:file_conflict_modal.conflicts_label">
        Conflicting files:
      </TranslationComponent>
    </TextBody>
    <Spacer size="2xsmall" />
    <ul data-test-id="ps-file-conflict-modal-list">
      {conflicts.map((name) => (
        <li key={name}>
          <TextBody variant="secondary" size="small">
            {name}
          </TextBody>
        </li>
      ))}
    </ul>
    <ButtonGroup slot="footer">
      <Button
        test-id="ps-file-conflict-modal-cancel"
        width="full"
        variant="secondary"
        size="large"
        onClick={onCancel}
      >
        <TranslationComponent i18nKey="pro-studio/actions:file_conflict_modal.buttons.cancel">
          Cancel
        </TranslationComponent>
      </Button>
      <Button
        test-id="ps-file-conflict-modal-overwrite"
        width="full"
        variant="warning"
        size="large"
        onClick={onOverwrite}
      >
        <TranslationComponent i18nKey="pro-studio/actions:file_conflict_modal.buttons.overwrite">
          Overwrite Existing Files
        </TranslationComponent>
      </Button>
    </ButtonGroup>
  </GenericModal>
);
